"use client";

import { useState } from "react";
import { Switch, Text } from "@radix-ui/themes";
import NotificationManager from "../../components/notifications/NotificationManager";
import WatsonApi from "../../backends/WatsonApi";
import MarkdownEditor from "../../components/editors/MarkdownEditor";
import { useStores } from "../../stores/useStores";
import useSWR from "swr";
import SchoolBlocksLoadingIndicator from "../../components/utilities/SchoolBlocksLoadingIndicator";
import SendTestButton from "../UserSettings/SendTestButton";
import { Tooltip } from "radix-ui";
import styles from "./styles/WelcomeEmailTab.module.scss";

export function WelcomeEmailTab() {
  const { organizationStore, userStore } = useStores();

  const [enabled, setEnabled] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [isSendingTest, setIsSendingTest] = useState(false);

  const { data, isLoading, mutate } = useSWR(
    "fetch-welcome-settings",
    async () => {
      try {
        const client = await WatsonApi();
        const response =
          await client.apis.organizations.organizations_welcome_email_settings_list(
            {
              organization_pk: organizationStore.organization.id,
            },
          );
        if (response.obj.data.length > 0) {
          setMessage(response.obj.data[0].message);
          setEnabled(response.obj.data[0].enabled);
          return response.obj.data[0];
        } else {
          return {
            message: "",
            enabled: false,
          };
        }
      } catch {
        NotificationManager.error("Welcome Email Settings not found.");
        return {
          message: "",
          enabled: false,
        };
      }
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  const hasChanged = data
    ? message.trim() !== (data.message ?? "").trim()
    : false;

  const messageLengthIsZero = message.trim().length === 0;

  async function handleToggle(toggleEnable) {
    try {
      const client = await WatsonApi();
      const response =
        await client.apis.organizations.organizations_welcome_email_settings_partial_update(
          {
            organization_pk: organizationStore.currentOrganization.id,
            id: data.id,
            data: {
              enabled: toggleEnable,
            },
          },
        );
      setEnabled(response.obj.enabled);
      if (toggleEnable) {
        NotificationManager.success("Your Welcome Email has been enabled");
      } else {
        NotificationManager.success("Your Welcome Email has been disabled");
      }
    } catch {
      NotificationManager.error(
        "There was an error while enabling the Welcome Email",
      );
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    try {
      const client = await WatsonApi();
      const response =
        await client.apis.organizations.organizations_welcome_email_settings_create(
          {
            organization_pk: organizationStore.currentOrganization.id,
            data: {
              message,
              enabled,
            },
          },
        );

      const updatedSettings = response.obj;
      if (updatedSettings) {
        NotificationManager.success("Settings successfully updated!");
        await mutate();
      }
    } catch (e: any) {
      switch (e.statusCode) {
        case 400:
          NotificationManager.error("Bad Request: Settings not updated.");
          break;
        case 404:
          NotificationManager.error(
            "Not Found: Organization settings not found.",
          );
          break;
        case 500:
          NotificationManager.error(
            "Internal Server Error: Settings not updated.",
          );
          break;
        default:
          NotificationManager.error("Unknown Error: Settings not updated.");
          break;
      }
    }
  }

  async function handleSendTest() {
    setIsSendingTest(true);
    try {
      const userId = userStore.id;
      const client = await WatsonApi();
      await client.apis.organizations.organizations_users_send_test_welcome_email(
        {
          organization_pk: organizationStore.organization.id,
          id: userId,
          test_message: message,
        },
      );
      NotificationManager.success("Test email sent!");
    } catch (error) {
      NotificationManager.error("Failed to send test email.");
      console.error(error);
    } finally {
      setTimeout(() => {
        setIsSendingTest(false);
      }, 30000);
    }
  }

  const sendTestDisabled = isSendingTest || messageLengthIsZero;
  const toggleDisabled = data?.message.trim().length === 0;

  return (
    <div>
      <div className="sb-settings-block-content-heading">
        <h4>Welcome Email</h4>
      </div>
      {isLoading ? (
        <div style={{ textAlign: "center" }}>
          <SchoolBlocksLoadingIndicator fill={"$dark-gray"} />
        </div>
      ) : (
        <form id="welcome-email-form" onSubmit={handleSubmit}>
          <div className="panel panel-default">
            <div className="panel-heading">
              <h3 className="panel-title text-center">
                First Login Welcome Email
              </h3>
            </div>
            <div className="panel-body">
              <div className="row">
                <div className="col-lg-12">
                  Configure an email that is automatically sent to users on
                  their first login. Use the message editor below to compose the
                  email content.
                </div>
              </div>
              <div className="row" style={{ marginTop: "15px" }}>
                <div className="col-sm-3">
                  <label>Message</label>
                </div>
                <div className="col-sm-9">
                  <MarkdownEditor
                    value={message}
                    handleTextChange={(val) => {
                      setMessage(val);
                    }}
                    placeholder="Enter your welcome email message..."
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-sm-3"></div>
                <div className="col-sm-4">
                  <Tooltip.Root delayDuration={0}>
                    {sendTestDisabled && (
                      <Tooltip.Portal>
                        <Tooltip.Content className={styles.tooltip}>
                          {isSendingTest
                            ? "Please wait before sending another test email."
                            : "A valid message must entered before sending a test."}
                          <Tooltip.Arrow />
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    )}
                    <Tooltip.Trigger asChild>
                      <SendTestButton
                        channel="Email"
                        disabled={sendTestDisabled}
                        sendTestNotification={handleSendTest}
                        className={"btn sb-organization-color-element-bg"}
                      />
                    </Tooltip.Trigger>
                  </Tooltip.Root>
                  <button
                    type="submit"
                    className="btn sb-organization-color-element-bg"
                    name="submit"
                    disabled={!hasChanged || (messageLengthIsZero && enabled)}
                  >
                    Save Message
                  </button>
                </div>
              </div>
              <div className="row" style={{ marginTop: "15px" }}>
                <div className="col-sm-3">
                  <Text as="label" htmlFor="welcome-email-enabled">
                    Enable Welcome Email
                  </Text>
                </div>
                <div className="col-sm-4">
                  <Tooltip.Root delayDuration={0}>
                    {toggleDisabled && (
                      <Tooltip.Portal>
                        <Tooltip.Content className={styles.tooltip}>
                          {
                            "A valid message must be saved before enabling the Welcome Email."
                          }
                          <Tooltip.Arrow />
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    )}
                    <Tooltip.Trigger asChild>
                      <span>
                        <Switch
                          id="welcome-email-enabled"
                          disabled={toggleDisabled}
                          checked={enabled}
                          onCheckedChange={async (checked) => {
                            await handleToggle(checked);
                          }}
                        />
                      </span>
                    </Tooltip.Trigger>
                  </Tooltip.Root>
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
